import React, { useState, useEffect, useMemo } from 'react';
import {
  doc,
  addDoc,
  updateDoc,
  collection,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useUser } from './UserContext';
import { useClients, useLinks, useAfianzadoras, useClientContracts } from '../contexts/DataContext';
import { getEmisorNombre } from '../utils/calculations';
import { REQUIRED_DOCS, DOC_LABELS } from '../hooks/usePulsoData';
import { Dialog, DialogTitle, DialogBody, DialogActions } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Badge } from './badge';
import { Text } from './text';
import FileReceiver from './FileReceiver';
import SearchableListbox from './SearchableListbox';
import {
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  UserIcon,
  BuildingOfficeIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';

export const OS_DOCS_PERSONA_FISICA = [
  { id: 'csf', label: 'Constancia de Situacion Fiscal' },
  { id: 'comprobanteDomicilio', label: 'Comprobante de Domicilio' },
  { id: 'identificacionOficial', label: 'Identificacion Oficial' },
  { id: 'declaracionAnual', label: 'Declaracion Anual con Acuse' },
  { id: 'estadosFinancierosAnuales', label: 'Estados Financieros Anuales (con leyenda, firmas, cedula CP)' },
  { id: 'curriculumVitae', label: 'Curriculum Vitae' },
  { id: 'estadosFinancierosParciales', label: 'Estados Financieros Parciales (no mayor a 3 meses)' },
];

// Docs that are per-afianzadora (one per OS-afianzadora link)
export const OS_AFIANZADORA_DOCS = [
  { id: 'entrevista', label: 'Entrevista' },
  { id: 'consultaBuro', label: 'Consulta Buro' },
];

export const OS_DOCS_PERSONA_MORAL_EXTRA = [
  { id: 'actaConstitutiva', label: 'Acta Constitutiva' },
  { id: 'actasAsambleas', label: 'Actas de Asambleas' },
  { id: 'poderes', label: 'Poderes' },
  { id: 'cedulaContador', label: 'Cedula Profesional del Contador' },
];

export function getOsDocs(tipo) {
  if (tipo === 'persona_moral') {
    return [...OS_DOCS_PERSONA_FISICA, ...OS_DOCS_PERSONA_MORAL_EXTRA];
  }
  return OS_DOCS_PERSONA_FISICA;
}

const STATUS_BADGE = {
  pendiente: { color: 'zinc', label: 'Pendiente' },
  subido: { color: 'blue', label: 'Subido' },
  procesando: { color: 'amber', label: 'Procesando' },
  procesado: { color: 'green', label: 'Procesado' },
};

const ObligadoSolidarioDialog = ({
  open,
  onClose,
  clientId,
  osData = null,       // null = create mode, object = edit/view mode
  onCreated,           // callback after creation
}) => {
  const { user, userData } = useUser();
  const { clientsList, clientsById } = useClients();
  const { linksById } = useLinks();
  const { afianzadorasById: globalAfianzadorasMap } = useAfianzadoras();
  const isCreate = !osData;

  // Create mode state
  const [createMode, setCreateMode] = useState('nuevo'); // 'nuevo' | 'existente'
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState('persona_fisica');
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Upload state for a specific file slot
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [showUploadFor, setShowUploadFor] = useState(null);

  // Afianzadora registrations for client-ref OS
  const [refClientAfianzadoras, setRefClientAfianzadoras] = useState([]);

  useEffect(() => {
    if (!open || !osData?.clienteRef) {
      setRefClientAfianzadoras([]);
      return;
    }
    const unsub = onSnapshot(
      collection(db, 'clientes', osData.clienteRef, 'afianzadoras'),
      (snap) => setRefClientAfianzadoras(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error('Error listening to OS client afianzadoras:', err)
    );
    return () => unsub();
  }, [open, osData?.clienteRef]);

  useEffect(() => {
    if (open && osData) {
      setNombre(osData.nombre || '');
      setTipo(osData.tipo || 'persona_fisica');
    } else if (open && !osData) {
      setNombre('');
      setTipo('persona_fisica');
      setCreateMode('nuevo');
      setSelectedClientId(null);
    }
    setUploadFile(null);
    setShowUploadFor(null);
  }, [open, osData]);

  const requiredDocs = getOsDocs(tipo);

  // Client options for "existing client" mode (exclude current client)
  const clientOptions = useMemo(() => {
    return clientsList
      .filter((c) => c.id !== clientId)
      .map((c) => ({ value: c.id, label: c.nombreEmpresa || c.nombre || c.id }));
  }, [clientsList, clientId]);

  const selectedClient = useMemo(() => {
    if (!selectedClientId) return null;
    return clientsList.find((c) => c.id === selectedClientId) || null;
  }, [clientsList, selectedClientId]);

  // Compute doc status for client-referenced OS (view mode)
  const refClientDocStatus = useMemo(() => {
    const refId = osData?.clienteRef;
    if (!refId) return null;
    const refClient = clientsById[refId];
    if (!refClient) return null;

    const clientLinks = (refClient.links || []).map((id) => linksById[id]).filter(Boolean);
    if (clientLinks.length === 0) return { docs: [], hasExpediente: false };

    const uploaded = new Set();
    const requested = new Set();

    for (const link of clientLinks) {
      const files = link.files || {};
      for (const docType of Object.keys(files)) {
        const fileInfo = files[docType];
        if (!fileInfo?.required) continue;
        requested.add(docType);
        if (fileInfo.uploadedCount > 0) uploaded.add(docType);
      }
    }

    const docs = REQUIRED_DOCS.map((docType) => {
      const isRequested = requested.has(docType);
      const isUploaded = uploaded.has(docType);
      let status = 'not_requested';
      if (isRequested) status = isUploaded ? 'ok' : 'missing';
      return { docType, status, label: DOC_LABELS[docType] || docType };
    });

    return { docs, hasExpediente: true };
  }, [osData?.clienteRef, clientsById, linksById]);

  // Contracts for the referenced client to compute afianzado per afianzadora
  const refClientContracts = useClientContracts(osData?.clienteRef || null);

  const afianzadoraLineaInfo = useMemo(() => {
    if (!refClientAfianzadoras.length) return {};
    const now = Date.now();
    // Collect active fianzas from the referenced client
    const activeFianzas = [];
    for (const contract of refClientContracts) {
      for (const fianza of (contract.fianzas || [])) {
        const emisorNombre = getEmisorNombre(fianza);
        if (!emisorNombre) continue;
        const fin = fianza.finVigenciaFianza || fianza.xmlFianza?.finVigenciaFianza;
        const finTs = typeof fin === 'number' ? fin : fin ? new Date(fin).getTime() : 0;
        if (finTs < now) continue;
        activeFianzas.push({
          _emisorNombre: emisorNombre.trim().toLowerCase(),
          montoAfianzado: fianza.montoAfianzado || fianza.xmlFianza?.montoAfianzado || 0,
        });
      }
    }

    const info = {};
    for (const ca of refClientAfianzadoras) {
      const global = globalAfianzadorasMap[ca.afianzadoraId];
      if (!global?.nombre) { info[ca.id] = { linea: ca.lineaAfianzamiento, afianzado: 0 }; continue; }
      const catalogName = global.nombre.trim().toLowerCase();
      const matched = activeFianzas.filter((f) => f._emisorNombre.includes(catalogName));
      const afianzado = matched.reduce((sum, f) => sum + f.montoAfianzado, 0);
      info[ca.id] = { linea: ca.lineaAfianzamiento, afianzado };
    }
    return info;
  }, [refClientAfianzadoras, refClientContracts, globalAfianzadorasMap]);

  const handleCreate = async () => {
    if (createMode === 'nuevo' && !nombre.trim()) return;
    if (createMode === 'existente' && !selectedClientId) return;
    setSaving(true);
    try {
      const colRef = collection(db, 'clientes', clientId, 'obligados_solidarios');

      if (createMode === 'existente') {
        // Link to existing client — no files needed, docs live in the client's expediente
        const clientNombre = selectedClient?.nombreEmpresa || selectedClient?.nombre || '';
        await addDoc(colRef, {
          nombre: clientNombre,
          tipo: 'cliente_existente',
          clienteRef: selectedClientId,
          createdAt: serverTimestamp(),
          createdBy: user.uid,
          org: userData.org,
          files: {},
        });
      } else {
        // New standalone OS with its own file slots
        const files = {};
        requiredDocs.forEach((d) => {
          files[d.id] = { status: 'pendiente', fileName: '', uploadedAt: null, storagePath: '' };
        });
        await addDoc(colRef, {
          nombre: nombre.trim(),
          tipo,
          clienteRef: null,
          createdAt: serverTimestamp(),
          createdBy: user.uid,
          org: userData.org,
          files,
        });
      }
      onCreated?.();
      onClose();
    } catch (err) {
      console.error('Error creating OS:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (fileType) => {
    if (!uploadFile || !osData) return;
    setUploadingSlot(fileType);
    try {
      const storagePath = `clientes/${clientId}/obligados_solidarios/${osData.id}/${fileType}/${uploadFile.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, uploadFile);

      const docRef = doc(db, 'clientes', clientId, 'obligados_solidarios', osData.id);
      await updateDoc(docRef, {
        [`files.${fileType}.status`]: 'subido',
        [`files.${fileType}.fileName`]: uploadFile.name,
        [`files.${fileType}.uploadedAt`]: serverTimestamp(),
        [`files.${fileType}.storagePath`]: storagePath,
      });

      setUploadFile(null);
      setShowUploadFor(null);
    } catch (err) {
      console.error('Error uploading OS file:', err);
    } finally {
      setUploadingSlot(null);
    }
  };

  const handleDownload = async (storagePath) => {
    if (!storagePath) return;
    try {
      const fileRef = ref(storage, storagePath);
      const url = await getDownloadURL(fileRef);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Error al abrir el archivo.');
    }
  };

  const isClientRef = osData?.clienteRef || osData?.tipo === 'cliente_existente';

  const canCreate = createMode === 'nuevo' ? !!nombre.trim() : !!selectedClientId;

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle>
        {isCreate ? 'Agregar Obligado Solidario' : `Obligado Solidario — ${osData?.nombre}`}
      </DialogTitle>
      <DialogBody>
        {isCreate ? (
          <div className="space-y-4">
            {/* Mode toggle */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">Origen</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setCreateMode('nuevo')}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                    createMode === 'nuevo'
                      ? 'border-azul-surelia bg-azul-surelia/5 text-azul-surelia'
                      : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'
                  }`}
                >
                  <UserIcon className="w-4 h-4" />
                  Nuevo
                </button>
                <button
                  onClick={() => setCreateMode('existente')}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                    createMode === 'existente'
                      ? 'border-azul-surelia bg-azul-surelia/5 text-azul-surelia'
                      : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'
                  }`}
                >
                  <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                  Cliente existente
                </button>
              </div>
            </div>

            {createMode === 'nuevo' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Nombre</label>
                  <Input
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    placeholder="Nombre completo o razon social"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Tipo</label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setTipo('persona_fisica')}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                        tipo === 'persona_fisica'
                          ? 'border-azul-surelia bg-azul-surelia/5 text-azul-surelia'
                          : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'
                      }`}
                    >
                      <UserIcon className="w-4 h-4" />
                      Persona Fisica
                    </button>
                    <button
                      onClick={() => setTipo('persona_moral')}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                        tipo === 'persona_moral'
                          ? 'border-azul-surelia bg-azul-surelia/5 text-azul-surelia'
                          : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'
                      }`}
                    >
                      <BuildingOfficeIcon className="w-4 h-4" />
                      Persona Moral
                    </button>
                  </div>
                </div>
                <div className="mt-3">
                  <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">
                    Documentos requeridos ({requiredDocs.length})
                  </h4>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-zinc-500 px-3 pt-1">Documentos personales</p>
                    {requiredDocs.map((d) => (
                      <div key={d.id} className="flex items-center gap-2 py-1.5 px-3 rounded-lg bg-zinc-50">
                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-300 flex-shrink-0" />
                        <span className="text-sm text-zinc-600">{d.label}</span>
                      </div>
                    ))}
                    <div className="border-t border-zinc-200 my-2" />
                    <p className="text-xs font-medium text-zinc-500 px-3 pt-1">Documentos por afianzadora</p>
                    {OS_AFIANZADORA_DOCS.map((d) => (
                      <div key={d.id} className="flex items-center gap-2 py-1.5 px-3 rounded-lg bg-zinc-50">
                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-300 flex-shrink-0" />
                        <span className="text-sm text-zinc-600">{d.label}</span>
                        <span className="text-[10px] text-zinc-400">(al vincular)</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">Selecciona un cliente</label>
                <SearchableListbox
                  options={clientOptions}
                  value={selectedClientId}
                  onChange={setSelectedClientId}
                  placeholder="Buscar cliente..."
                  searchPlaceholder="Buscar por nombre..."
                  aria-label="Seleccionar cliente existente"
                  popupClassName="w-full"
                />
                <Text className="text-zinc-400 text-xs mt-2">
                  Los documentos del obligado solidario seran los del expediente de este cliente.
                </Text>
              </div>
            )}
          </div>
        ) : isClientRef ? (
          /* View mode for client-referenced OS */
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-zinc-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center">
                  <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold text-zinc-900">{osData.nombre}</p>
                  <p className="text-xs text-zinc-400">Cliente existente</p>
                </div>
              </div>
              <Button
                outline
                color="surelia"
                onClick={() => window.open(`/app/clientes/${osData.clienteRef}`, '_blank')}
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-1" />
                Ver expediente
              </Button>
            </div>

            {/* Document status grid */}
            {refClientDocStatus && !refClientDocStatus.hasExpediente ? (
              <div className="bg-zinc-50 rounded-lg p-4 text-center">
                <Text className="text-zinc-500 text-sm">Este cliente aun no tiene expediente creado.</Text>
              </div>
            ) : refClientDocStatus ? (
              <div>
                <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Estado de documentos</h4>
                <div className="grid grid-cols-2 gap-2">
                  {refClientDocStatus.docs.filter((d) => d.status !== 'not_requested').map((d) => (
                    <div
                      key={d.docType}
                      className={`flex items-center gap-2 p-2 rounded-lg border ${
                        d.status === 'ok'
                          ? 'bg-green-50 border-green-200'
                          : 'bg-gray-100 border-gray-300'
                      }`}
                    >
                      {d.status === 'ok' ? (
                        <CheckCircleIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                      ) : (
                        <XCircleIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className={`text-xs font-medium truncate ${
                          d.status === 'ok' ? 'text-green-700' : 'text-gray-600'
                        }`}>
                          {d.label}
                        </p>
                        <p className={`text-[10px] ${
                          d.status === 'ok' ? 'text-green-600' : 'text-gray-500'
                        }`}>
                          {d.status === 'ok' ? 'Completo' : 'Faltante'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                {refClientDocStatus.docs.every((d) => d.status === 'not_requested') && (
                  <Text className="text-zinc-400 text-sm text-center py-3">No se han solicitado documentos a este cliente.</Text>
                )}
              </div>
            ) : null}

            {/* Afianzadora registrations for this OS client */}
            {refClientAfianzadoras.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">
                  Afianzadoras registradas ({refClientAfianzadoras.length}) — Línea de Afianzamiento
                </h4>
                <div className="space-y-1">
                  {refClientAfianzadoras.map((ca) => {
                    const global = globalAfianzadorasMap[ca.afianzadoraId];
                    const nombre = global?.nombre || 'Afianzadora desconocida';
                    const logo = global?.logo || null;
                    const info = afianzadoraLineaInfo[ca.id] || {};
                    const linea = info.linea;
                    const afianzado = info.afianzado || 0;
                    const disponible = linea ? linea - afianzado : null;
                    const fmt = (v) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(v);
                    return (
                      <div key={ca.id} className="py-2 px-3 rounded-lg bg-zinc-50">
                        <div className="flex items-center gap-2 mb-1.5">
                          {logo ? (
                            <img src={logo} alt={nombre} className="w-6 h-6 rounded object-contain bg-white border border-zinc-100 p-0.5" />
                          ) : (
                            <div className="w-6 h-6 rounded bg-azul-surelia/10 flex items-center justify-center text-azul-surelia font-bold text-[10px]">
                              {nombre.charAt(0)}
                            </div>
                          )}
                          <span className="text-sm font-medium text-zinc-700">{nombre}</span>
                        </div>
                        {linea ? (
                          <div className="grid grid-cols-3 gap-2 ml-8 text-xs">
                            <div>
                              <span className="text-zinc-400">Línea</span>
                              <p className="font-semibold text-zinc-700">{fmt(linea)}</p>
                            </div>
                            <div>
                              <span className="text-zinc-400">Afianzado</span>
                              <p className="font-semibold text-zinc-700">{fmt(afianzado)}</p>
                            </div>
                            <div>
                              <span className="text-zinc-400">Disponible</span>
                              <p className={`font-semibold ${disponible >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmt(disponible)}</p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-zinc-400 ml-8">Sin línea de afianzamiento</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* View mode for standalone OS */
          <div className="space-y-4">
            {/* OS info header */}
            <div className="flex items-center gap-3 pb-3 border-b border-zinc-100">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                osData.tipo === 'persona_moral' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
              }`}>
                {osData.tipo === 'persona_moral' ? (
                  <BuildingOfficeIcon className="w-5 h-5" />
                ) : (
                  <UserIcon className="w-5 h-5" />
                )}
              </div>
              <div>
                <p className="font-semibold text-zinc-900">{osData.nombre}</p>
                <p className="text-xs text-zinc-400">
                  {osData.tipo === 'persona_moral' ? 'Persona Moral' : 'Persona Fisica'}
                </p>
              </div>
            </div>

            {/* Document slots */}
            <div>
              <h4 className="text-sm font-semibold text-zinc-700 mb-2">Documentos</h4>
              <div className="space-y-2">
                {requiredDocs.map((d) => {
                  const fileData = osData.files?.[d.id] || { status: 'pendiente' };
                  const statusInfo = STATUS_BADGE[fileData.status] || STATUS_BADGE.pendiente;
                  const isUploading = uploadingSlot === d.id;

                  return (
                    <div key={d.id}>
                      <div className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg bg-zinc-50">
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-zinc-700">{d.label}</span>
                          {fileData.fileName && (
                            <span className="block text-xs text-zinc-400 truncate">{fileData.fileName}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge color={statusInfo.color}>{statusInfo.label}</Badge>
                          {fileData.storagePath && (
                            <button
                              onClick={() => handleDownload(fileData.storagePath)}
                              className="p-1.5 rounded-lg text-azul-surelia hover:bg-azul-surelia/10 transition-colors"
                              title="Descargar"
                            >
                              <ArrowDownTrayIcon className="w-4 h-4" />
                            </button>
                          )}
                          {fileData.status === 'pendiente' && (
                            <button
                              onClick={() => {
                                setShowUploadFor(showUploadFor === d.id ? null : d.id);
                                setUploadFile(null);
                              }}
                              className="p-1.5 rounded-lg text-azul-surelia hover:bg-azul-surelia/10 transition-colors"
                              title={`Subir ${d.label}`}
                            >
                              <ArrowUpTrayIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      {/* Inline upload area */}
                      {showUploadFor === d.id && (
                        <div className="mt-2 ml-3 mr-3 mb-2">
                          <FileReceiver
                            title=""
                            type="image-or-pdf"
                            maxFiles={1}
                            onFilesChange={(files) => setUploadFile(files[0] || null)}
                            showFileName
                            currentFile={uploadFile}
                            onRemoveFile={() => setUploadFile(null)}
                          />
                          {uploadFile && (
                            <div className="mt-2 flex justify-end">
                              <Button
                                color="surelia"
                                onClick={() => handleUpload(d.id)}
                                disabled={isUploading}
                              >
                                {isUploading ? 'Subiendo...' : 'Subir'}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          {isCreate ? 'Cancelar' : 'Cerrar'}
        </Button>
        {isCreate && (
          <Button
            color="surelia"
            onClick={handleCreate}
            disabled={!canCreate || saving}
          >
            {saving ? 'Guardando...' : 'Crear'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ObligadoSolidarioDialog;
